/**
 * The scope, always stated in words, always one click from being changed.
 *
 * Scope-setting is episodic and verification is constant, so scope gets a sentence
 * and the evidence gets a permanent rail — not the other way round.
 */
import { useTranslation } from "react-i18next";

import { formatCount } from "@/i18n/index.ts";
import { type NotebookScope } from "@/lib/notebookScope.ts";

/**
 * What the notebook will answer from, in one sentence.
 *
 * Reads the NOTEBOOK's sources directly. It used to read a parallel picker-model scope
 * kept in step by a bridge — two representations of one fact, which is one more than a
 * sentence describing that fact needs.
 */
export function scopeSentence(
  scope: NotebookScope,
  corpusTotal: number | null,
  t: (key: string, options?: Record<string, unknown>) => string,
  language: string,
): string {
  const collections = scope.collectionIds.length;
  const documents = scope.documentIds.length;
  if (!scope.wholeCorpus && collections === 0 && documents === 0) return t("scope.empty");
  // The whole corpus and nothing else: say so with its size rather than listing nothing.
  if (scope.wholeCorpus && collections === 0 && documents === 0) {
    return corpusTotal === null
      ? t("scope.all")
      : t("scope.allWithCount", { count: formatCount(corpusTotal, language) });
  }
  const pieces = [scope.wholeCorpus ? t("scope.all") : t("scope.pustakaOff")];
  if (collections > 0) pieces.push(t("scope.collections", { count: collections }));
  if (documents > 0) pieces.push(t("scope.documents", { count: documents }));
  return pieces.join(" · ");
}

export function ScopeBar({
  scope,
  corpusTotal,
  onEdit,
}: {
  scope: NotebookScope;
  corpusTotal: number | null;
  onEdit: () => void;
}) {
  const { t, i18n } = useTranslation();
  const sentence = scopeSentence(scope, corpusTotal, t, i18n.language);

  return (
    <button type="button" className="scopebar" onClick={onEdit}>
      <span className="scope-dot" aria-hidden="true" />
      {/* A bare text node here became a flex item of its own and fragmented the bar. */}
      <span className="scope-text">
        <span className="faint">{t("scope.bar")}: </span>
        <strong>{sentence}</strong>
      </span>
      <span className="scope-edit">{t("scope.edit")}</span>
    </button>
  );
}
